/**
 * PublicCookieBanner — Iter 191h.
 *
 * Storefront-side consent banner rendered by PublicSitePage. Reads the
 * vendor's config from `site.settings.cookies` (shape defined in
 * `settings/SettingsCookies.js`). Persists visitor choice in localStorage
 * keyed per subdomain so it doesn't reappear on every page load.
 *
 * The banner is intentionally self-contained — no external deps beyond
 * lucide-react (already installed) — so it can be safely embedded in the
 * public renderer without breaking builds.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Cookie, X, Settings2 } from 'lucide-react';

const STORAGE_KEY_PREFIX = 'entrepren_cookie_consent:';

function loadConsent(subdomain) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + subdomain);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function saveConsent(subdomain, value) {
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + subdomain, JSON.stringify(value));
  } catch { /* private mode */ }
}

export default function PublicCookieBanner({ cookies, subdomain = 'default' }) {
  const cfg = useMemo(() => ({
    enabled: true,
    mode: 'opt-in',
    position: 'bottom-left',
    theme: 'dark',
    headline: 'A note on cookies',
    body: 'We use cookies to improve your experience. Accept all, decline non-essential, or manage preferences.',
    accept_label: 'Accept all',
    decline_label: 'Decline',
    preferences_label: 'Preferences',
    show_decline: true,
    show_preferences: false,
    privacy_link: '/privacy',
    categories: { essential: true, analytics: true, marketing: true, personalization: false },
    ...(cookies || {}),
  }), [cookies]);

  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({
    analytics: cfg.categories?.analytics ?? true,
    marketing: cfg.categories?.marketing ?? true,
    personalization: cfg.categories?.personalization ?? false,
  });

  useEffect(() => {
    if (!cfg.enabled) return;
    const existing = loadConsent(subdomain);
    if (!existing) setVisible(true);
  }, [cfg.enabled, subdomain]);

  if (!cfg.enabled || !visible) return null;

  const isDark = cfg.theme === 'dark';
  const isBar = cfg.position === 'bottom-bar' || cfg.position === 'top-bar';

  const containerPos =
    cfg.position === 'top-bar' ? 'top-0 left-0 right-0' :
    cfg.position === 'bottom-bar' ? 'bottom-0 left-0 right-0' :
    cfg.position === 'bottom-right' ? 'bottom-4 right-4 sm:max-w-md' :
    'bottom-4 left-4 sm:max-w-md';

  const shellBase = isBar
    ? 'flex flex-col sm:flex-row items-start sm:items-center gap-3 px-5 py-3'
    : 'p-4 rounded-2xl';

  const shellColor = isDark
    ? 'bg-[#0B0B24]/95 border-white/10 text-white shadow-[0_20px_60px_-15px_rgba(0,0,0,0.6)]'
    : 'bg-white/95 border-black/10 text-[#0E0E23] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.25)]';

  const secondaryBtn = isDark
    ? 'bg-white/5 border border-white/15 text-white hover:bg-white/10'
    : 'bg-black/5 border border-black/10 text-[#0E0E23] hover:bg-black/10';

  const acceptAll = () => {
    saveConsent(subdomain, {
      decision: 'accept-all',
      at: new Date().toISOString(),
      categories: { essential: true, analytics: true, marketing: true, personalization: true },
    });
    setVisible(false);
  };
  const declineAll = () => {
    saveConsent(subdomain, {
      decision: 'decline-all',
      at: new Date().toISOString(),
      categories: { essential: true, analytics: false, marketing: false, personalization: false },
    });
    setVisible(false);
  };
  const saveCustom = () => {
    saveConsent(subdomain, {
      decision: 'custom',
      at: new Date().toISOString(),
      categories: { essential: true, ...prefs },
    });
    setVisible(false);
  };

  return (
    <div
      className={`fixed z-[9999] backdrop-blur-md border ${shellColor} ${shellBase} ${containerPos}`}
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="public-cookie-banner"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-[13px] font-bold">
          <Cookie size={14} className={isDark ? 'text-[#A78BFA]' : 'text-[#6D28D9]'} />
          {cfg.headline}
        </div>
        <p className={`mt-1 text-[12px] leading-snug ${isDark ? 'text-white/70' : 'text-[#0E0E23]/75'}`}>
          {cfg.body}{' '}
          {cfg.privacy_link && (
            <a
              href={cfg.privacy_link}
              className={`underline ${isDark ? 'text-[#A78BFA] hover:text-white' : 'text-[#6D28D9] hover:text-[#4C1D95]'}`}
              data-testid="cookie-privacy-link"
            >
              Privacy
            </a>
          )}
        </p>

        {showPrefs && (
          <div className={`mt-3 rounded-lg p-3 ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]'}`}>
            <PrefRow label="Essential" hint="Required" checked disabled />
            <PrefRow
              label="Analytics"
              hint="Page views & performance"
              checked={prefs.analytics}
              onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
              testid="pref-analytics"
              isDark={isDark}
            />
            <PrefRow
              label="Marketing"
              hint="Ad pixels & retargeting"
              checked={prefs.marketing}
              onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
              testid="pref-marketing"
              isDark={isDark}
            />
            <PrefRow
              label="Personalization"
              hint="Saved preferences"
              checked={prefs.personalization}
              onChange={(v) => setPrefs((p) => ({ ...p, personalization: v }))}
              testid="pref-personalization"
              isDark={isDark}
            />
          </div>
        )}
      </div>

      <div className={`flex items-center gap-2 ${isBar ? '' : 'mt-3'} shrink-0 flex-wrap`}>
        {cfg.show_preferences && !showPrefs && (
          <button
            type="button"
            onClick={() => setShowPrefs(true)}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-semibold ${secondaryBtn}`}
            data-testid="cookie-preferences-btn"
          >
            <Settings2 size={11} /> {cfg.preferences_label}
          </button>
        )}
        {showPrefs && (
          <button
            type="button"
            onClick={saveCustom}
            className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold ${secondaryBtn}`}
            data-testid="cookie-save-prefs-btn"
          >
            Save preferences
          </button>
        )}
        {cfg.show_decline && (
          <button
            type="button"
            onClick={declineAll}
            className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold ${secondaryBtn}`}
            data-testid="cookie-decline-btn"
          >
            {cfg.decline_label}
          </button>
        )}
        <button
          type="button"
          onClick={acceptAll}
          className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-[#A78BFA] text-[#0E0E23] hover:bg-white transition-colors"
          data-testid="cookie-accept-btn"
        >
          {cfg.accept_label}
        </button>
        {!isBar && (
          <button
            type="button"
            onClick={declineAll}
            aria-label="Dismiss"
            className={isDark ? 'text-white/40 hover:text-white' : 'text-[#0E0E23]/40 hover:text-[#0E0E23]'}
          >
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function PrefRow({ label, hint, checked, onChange, disabled = false, testid, isDark = true }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <div>
        <div className={`text-[11.5px] font-semibold ${isDark ? 'text-white' : 'text-[#0E0E23]'}`}>{label}</div>
        <div className={`text-[10px] ${isDark ? 'text-white/50' : 'text-[#0E0E23]/50'}`}>{hint}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => !disabled && onChange && onChange(!checked)}
        disabled={disabled}
        data-testid={testid}
        className={`relative w-8 h-5 rounded-full transition-colors ${
          checked ? 'bg-[#A78BFA]' : isDark ? 'bg-white/15' : 'bg-black/15'
        } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span
          className={`absolute top-[2px] left-[2px] w-4 h-4 rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-3' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}
